/**
 * wikiGlRenderer — WebGL layer for the wiki full-map graph.
 *
 * Obsidian renders its graph view on the GPU, which is what makes the layout
 * unfold, drag and zoom feel liquid. This renderer does the same for our
 * heavy layers: edges as GL_LINES (hairline, screen-constant) and nodes as
 * anti-aliased point sprites (circle SDF in the fragment shader), so a
 * 10k-edge wiki redraws in well under a millisecond of GPU time every frame.
 * Text (labels, current-note focus) stays on the 2D overlay canvas above.
 *
 * The engine falls back to its 2D bitmap pipeline when a context can't be
 * created or is lost — this class only reports `ok`.
 */

export interface GlCamera { scale: number; ox: number; oy: number }

export interface GlRenderState {
  /** Uniform node fill (full-map gray). */
  nodeColor: string;
  /** Focus purple for the hovered node + its incident edges/aura. */
  highlight: string;
  /** Accent (blue) for visited notes. */
  accent: string;
  /** Index of the hovered node, or -1. */
  hoverIndex: number;
  /** Indices lit while hovering (hovered + neighbors). */
  litIndices: number[] | null;
  /** Draw the soft aura pass (skipped when zoomed far out). */
  aura: boolean;
}

const VS = `
attribute vec2 a_pos;
attribute float a_size;
attribute float a_kind;
uniform vec3 u_cam;      // scale, ox, oy (css px)
uniform vec2 u_view;     // viewport css px
uniform float u_dpr;
uniform float u_sizeMul;
varying float v_kind;
void main() {
  vec2 screen = a_pos * u_cam.x + u_cam.yz;
  vec2 clip = (screen / u_view) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  gl_PointSize = max(a_size * u_cam.x * u_sizeMul, 1.5) * 2.0 * u_dpr;
  v_kind = a_kind;
}`;

const FS_NODE = `
precision mediump float;
varying float v_kind;
uniform vec4 u_color;    // base fill (premultiplied at draw time below)
uniform vec4 u_accent;   // visited fill
uniform float u_alpha;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float d = length(c) * 2.0;
  float edge = fwidth(d) * 1.5;
  float a = 1.0 - smoothstep(1.0 - edge, 1.0, d);
  if (a <= 0.001) discard;
  vec4 col = mix(u_color, u_accent, step(0.5, v_kind));
  float A = col.a * a * u_alpha;
  gl_FragColor = vec4(col.rgb * A, A);
}`;

// fwidth needs the OES_standard_derivatives extension in WebGL1; the fallback
// shader uses a fixed feather width instead.
const FS_NODE_NODERIV = FS_NODE.replace('float edge = fwidth(d) * 1.5;', 'float edge = 0.08;');

const FS_EDGE = `
precision mediump float;
uniform vec4 u_color;
uniform float u_alpha;
void main() {
  float A = u_color.a * u_alpha;
  gl_FragColor = vec4(u_color.rgb * A, A);
}`;

const VS_EDGE = `
attribute vec2 a_pos;
uniform vec3 u_cam;
uniform vec2 u_view;
void main() {
  vec2 screen = a_pos * u_cam.x + u_cam.yz;
  vec2 clip = (screen / u_view) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
}`;

function hexToRgba(hex: string, alpha = 1): [number, number, number, number] {
  const h = hex.replace('#', '');
  const v = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255, alpha];
}

interface NodeInput { x: number; y: number; r: number }

export class WikiGlRenderer {
  ok = false;
  private gl: WebGLRenderingContext | null = null;
  private cv: HTMLCanvasElement;
  private nodeProg: WebGLProgram | null = null;
  private edgeProg: WebGLProgram | null = null;
  private nodeLoc!: Record<string, WebGLUniformLocation | null>;
  private edgeLoc!: Record<string, WebGLUniformLocation | null>;
  private nodeAttrs!: { pos: number; size: number; kind: number };
  private edgeAttr = 0;

  private bufNodePos: WebGLBuffer | null = null;
  private bufNodeSize: WebGLBuffer | null = null;
  private bufNodeKind: WebGLBuffer | null = null;
  private bufEdgePos: WebGLBuffer | null = null;
  private bufLitNodePos: WebGLBuffer | null = null;
  private bufLitNodeSize: WebGLBuffer | null = null;
  private bufLitEdgePos: WebGLBuffer | null = null;
  private bufHoverPos: WebGLBuffer | null = null;
  private bufHoverSize: WebGLBuffer | null = null;

  private nodeCount = 0;
  private edgeVertCount = 0;
  private litNodeCount = 0;
  private litEdgeVertCount = 0;
  private lastLitHover = -2;

  private edgePairs: Int32Array = new Int32Array(0);
  private nodePosArr: Float32Array = new Float32Array(0);
  private nodeSizeArr: Float32Array = new Float32Array(0);
  private edgePosArr: Float32Array = new Float32Array(0);

  constructor(canvas: HTMLCanvasElement) {
    this.cv = canvas;
    const gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
      // Keep the last frame across composites so idle frames can skip work.
      preserveDrawingBuffer: true,
    }) as WebGLRenderingContext | null;
    if (!gl) return;
    this.gl = gl;

    const hasDeriv = !!gl.getExtension('OES_standard_derivatives');
    this.nodeProg = this.buildProgram(VS, hasDeriv
      ? `#extension GL_OES_standard_derivatives : enable\n${FS_NODE}`
      : FS_NODE_NODERIV);
    this.edgeProg = this.buildProgram(VS_EDGE, FS_EDGE);
    if (!this.nodeProg || !this.edgeProg) return;

    this.nodeLoc = {
      cam: gl.getUniformLocation(this.nodeProg, 'u_cam'),
      view: gl.getUniformLocation(this.nodeProg, 'u_view'),
      dpr: gl.getUniformLocation(this.nodeProg, 'u_dpr'),
      sizeMul: gl.getUniformLocation(this.nodeProg, 'u_sizeMul'),
      color: gl.getUniformLocation(this.nodeProg, 'u_color'),
      accent: gl.getUniformLocation(this.nodeProg, 'u_accent'),
      alpha: gl.getUniformLocation(this.nodeProg, 'u_alpha'),
    };
    this.edgeLoc = {
      cam: gl.getUniformLocation(this.edgeProg, 'u_cam'),
      view: gl.getUniformLocation(this.edgeProg, 'u_view'),
      color: gl.getUniformLocation(this.edgeProg, 'u_color'),
      alpha: gl.getUniformLocation(this.edgeProg, 'u_alpha'),
    };
    this.nodeAttrs = {
      pos: gl.getAttribLocation(this.nodeProg, 'a_pos'),
      size: gl.getAttribLocation(this.nodeProg, 'a_size'),
      kind: gl.getAttribLocation(this.nodeProg, 'a_kind'),
    };
    this.edgeAttr = gl.getAttribLocation(this.edgeProg, 'a_pos');

    this.bufNodePos = gl.createBuffer();
    this.bufNodeSize = gl.createBuffer();
    this.bufNodeKind = gl.createBuffer();
    this.bufEdgePos = gl.createBuffer();
    this.bufLitNodePos = gl.createBuffer();
    this.bufLitNodeSize = gl.createBuffer();
    this.bufLitEdgePos = gl.createBuffer();
    this.bufHoverPos = gl.createBuffer();
    this.bufHoverSize = gl.createBuffer();

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // premultiplied alpha

    canvas.addEventListener('webglcontextlost', () => { this.ok = false; });
    this.ok = true;
  }

  private buildProgram(vsSrc: string, fsSrc: string): WebGLProgram | null {
    const gl = this.gl!;
    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) return null;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) return null;
      return sh;
    };
    const vs = compile(gl.VERTEX_SHADER, vsSrc);
    const fs = compile(gl.FRAGMENT_SHADER, fsSrc);
    if (!vs || !fs) return null;
    const prog = gl.createProgram();
    if (!prog) return null;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    return prog;
  }

  /** (Re)load the graph structure: node sizes/kinds and the edge index pairs. */
  setGraph(nodes: NodeInput[], edgePairs: [number, number][], visited: Set<number>): void {
    const gl = this.gl;
    if (!gl || !this.ok) return;
    this.nodeCount = nodes.length;
    this.nodePosArr = new Float32Array(nodes.length * 2);
    this.nodeSizeArr = new Float32Array(nodes.length);
    const kinds = new Float32Array(nodes.length);
    for (let i = 0; i < nodes.length; i++) {
      this.nodeSizeArr[i] = nodes[i].r;
      kinds[i] = visited.has(i) ? 1 : 0;
    }
    this.edgePairs = new Int32Array(edgePairs.length * 2);
    for (let i = 0; i < edgePairs.length; i++) {
      this.edgePairs[i * 2] = edgePairs[i][0];
      this.edgePairs[i * 2 + 1] = edgePairs[i][1];
    }
    this.edgeVertCount = edgePairs.length * 2;
    this.edgePosArr = new Float32Array(this.edgeVertCount * 2);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufNodeSize);
    gl.bufferData(gl.ARRAY_BUFFER, this.nodeSizeArr, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufNodeKind);
    gl.bufferData(gl.ARRAY_BUFFER, kinds, gl.STATIC_DRAW);
    this.updatePositions(nodes);
    this.lastLitHover = -2;
  }

  /** Refill position buffers — called while the simulation is moving. */
  updatePositions(nodes: NodeInput[]): void {
    const gl = this.gl;
    if (!gl || !this.ok || nodes.length !== this.nodeCount) return;
    for (let i = 0; i < nodes.length; i++) {
      this.nodePosArr[i * 2] = nodes[i].x;
      this.nodePosArr[i * 2 + 1] = nodes[i].y;
    }
    for (let i = 0; i < this.edgePairs.length; i++) {
      const n = this.edgePairs[i];
      this.edgePosArr[i * 2] = this.nodePosArr[n * 2];
      this.edgePosArr[i * 2 + 1] = this.nodePosArr[n * 2 + 1];
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufNodePos);
    gl.bufferData(gl.ARRAY_BUFFER, this.nodePosArr, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufEdgePos);
    gl.bufferData(gl.ARRAY_BUFFER, this.edgePosArr, gl.DYNAMIC_DRAW);
    this.lastLitHover = -2; // lit subsets hold positions — rebuild on demand
  }

  /** Rebuild the hover subset buffers (lit nodes + incident edges). */
  private buildLit(hoverIndex: number, litIndices: number[]): void {
    const gl = this.gl!;
    const pos = new Float32Array(litIndices.length * 2);
    const size = new Float32Array(litIndices.length);
    for (let i = 0; i < litIndices.length; i++) {
      const n = litIndices[i];
      pos[i * 2] = this.nodePosArr[n * 2];
      pos[i * 2 + 1] = this.nodePosArr[n * 2 + 1];
      size[i] = this.nodeSizeArr[n];
    }
    this.litNodeCount = litIndices.length;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufLitNodePos);
    gl.bufferData(gl.ARRAY_BUFFER, pos, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufLitNodeSize);
    gl.bufferData(gl.ARRAY_BUFFER, size, gl.DYNAMIC_DRAW);

    // Incident edges of the hovered node.
    const verts: number[] = [];
    for (let i = 0; i < this.edgePairs.length; i += 2) {
      const a = this.edgePairs[i]; const b = this.edgePairs[i + 1];
      if (a !== hoverIndex && b !== hoverIndex) continue;
      verts.push(
        this.nodePosArr[a * 2], this.nodePosArr[a * 2 + 1],
        this.nodePosArr[b * 2], this.nodePosArr[b * 2 + 1],
      );
    }
    this.litEdgeVertCount = verts.length / 2;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufLitEdgePos);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.DYNAMIC_DRAW);
    this.lastLitHover = hoverIndex;
  }

  render(cam: GlCamera, state: GlRenderState): void {
    const gl = this.gl;
    if (!gl || !this.ok) return;
    const dpr = window.devicePixelRatio || 1;
    const vw = this.cv.clientWidth; const vh = this.cv.clientHeight;
    if (vw === 0 || vh === 0) return;
    if (this.cv.width !== vw * dpr || this.cv.height !== vh * dpr) {
      this.cv.width = vw * dpr; this.cv.height = vh * dpr;
    }
    gl.viewport(0, 0, this.cv.width, this.cv.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const hovering = state.hoverIndex >= 0;
    if (hovering && this.lastLitHover !== state.hoverIndex && state.litIndices) {
      this.buildLit(state.hoverIndex, state.litIndices);
    }

    const gray: [number, number, number, number] = [122 / 255, 132 / 255, 148 / 255, 1];
    const nodeCol = hexToRgba(state.nodeColor);
    const accentCol = hexToRgba(state.accent);
    const hlCol = hexToRgba(state.highlight);

    // --- Edges (all): whisper-faint; extra-faint while hovering.
    gl.useProgram(this.edgeProg);
    gl.uniform3f(this.edgeLoc.cam, cam.scale, cam.ox, cam.oy);
    gl.uniform2f(this.edgeLoc.view, vw, vh);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bufEdgePos);
    gl.enableVertexAttribArray(this.edgeAttr);
    gl.vertexAttribPointer(this.edgeAttr, 2, gl.FLOAT, false, 0, 0);
    // Lower alpha than the 2D pipeline's 0.15: Canvas2D composites a whole
    // path once (overlaps don't stack), GL blends every line independently,
    // so dense regions accumulate — 0.08 lands at the same visual weight.
    gl.uniform4f(this.edgeLoc.color, gray[0], gray[1], gray[2], 1);
    gl.uniform1f(this.edgeLoc.alpha, hovering ? 0.03 : 0.08);
    gl.drawArrays(gl.LINES, 0, this.edgeVertCount);

    // Hovered node's incident edges in purple.
    if (hovering && this.litEdgeVertCount > 0) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufLitEdgePos);
      gl.vertexAttribPointer(this.edgeAttr, 2, gl.FLOAT, false, 0, 0);
      gl.uniform4f(this.edgeLoc.color, hlCol[0], hlCol[1], hlCol[2], 1);
      gl.uniform1f(this.edgeLoc.alpha, 0.5);
      gl.drawArrays(gl.LINES, 0, this.litEdgeVertCount);
    }

    // --- Nodes: aura pass, then discs (kind 1 = visited → accent).
    gl.useProgram(this.nodeProg);
    gl.uniform3f(this.nodeLoc.cam, cam.scale, cam.ox, cam.oy);
    gl.uniform2f(this.nodeLoc.view, vw, vh);
    gl.uniform1f(this.nodeLoc.dpr, dpr);
    gl.uniform4f(this.nodeLoc.color, nodeCol[0], nodeCol[1], nodeCol[2], 1);
    gl.uniform4f(this.nodeLoc.accent, accentCol[0], accentCol[1], accentCol[2], 1);

    const bindNodeAttrs = (pos: WebGLBuffer | null, size: WebGLBuffer | null, kind: WebGLBuffer | null, kindConst?: number) => {
      gl.bindBuffer(gl.ARRAY_BUFFER, pos);
      gl.enableVertexAttribArray(this.nodeAttrs.pos);
      gl.vertexAttribPointer(this.nodeAttrs.pos, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, size);
      gl.enableVertexAttribArray(this.nodeAttrs.size);
      gl.vertexAttribPointer(this.nodeAttrs.size, 1, gl.FLOAT, false, 0, 0);
      if (kind) {
        gl.bindBuffer(gl.ARRAY_BUFFER, kind);
        gl.enableVertexAttribArray(this.nodeAttrs.kind);
        gl.vertexAttribPointer(this.nodeAttrs.kind, 1, gl.FLOAT, false, 0, 0);
      } else {
        gl.disableVertexAttribArray(this.nodeAttrs.kind);
        gl.vertexAttrib1f(this.nodeAttrs.kind, kindConst ?? 0);
      }
    };

    bindNodeAttrs(this.bufNodePos, this.bufNodeSize, this.bufNodeKind);
    if (state.aura) {
      gl.uniform1f(this.nodeLoc.sizeMul, 2.1);
      gl.uniform1f(this.nodeLoc.alpha, hovering ? 0.04 : 0.08);
      gl.drawArrays(gl.POINTS, 0, this.nodeCount);
    }
    gl.uniform1f(this.nodeLoc.sizeMul, 1);
    gl.uniform1f(this.nodeLoc.alpha, hovering ? 0.22 : 1);
    gl.drawArrays(gl.POINTS, 0, this.nodeCount);

    if (hovering && this.litNodeCount > 0) {
      // Lit neighbors back at full strength (gray/accent per kind is lost in
      // the subset — draw them uniform gray, matching the 2D fallback's look
      // closely enough; visited discs re-assert below via the main pass).
      bindNodeAttrs(this.bufLitNodePos, this.bufLitNodeSize, null, 0);
      gl.uniform1f(this.nodeLoc.sizeMul, 1);
      gl.uniform1f(this.nodeLoc.alpha, 1);
      gl.drawArrays(gl.POINTS, 0, this.litNodeCount);

      // Hovered node: purple aura + purple disc, slightly enlarged.
      const hi = state.hoverIndex;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufHoverPos);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([this.nodePosArr[hi * 2], this.nodePosArr[hi * 2 + 1]]), gl.DYNAMIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufHoverSize);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([this.nodeSizeArr[hi]]), gl.DYNAMIC_DRAW);
      bindNodeAttrs(this.bufHoverPos, this.bufHoverSize, null, 0);
      gl.uniform4f(this.nodeLoc.color, hlCol[0], hlCol[1], hlCol[2], 1);
      gl.uniform1f(this.nodeLoc.sizeMul, 2.4);
      gl.uniform1f(this.nodeLoc.alpha, 0.25);
      gl.drawArrays(gl.POINTS, 0, 1);
      gl.uniform1f(this.nodeLoc.sizeMul, 1.25);
      gl.uniform1f(this.nodeLoc.alpha, 1);
      gl.drawArrays(gl.POINTS, 0, 1);
    }
  }

  clear(): void {
    const gl = this.gl;
    if (!gl) return;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }
}
